import React from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { selectSettings } from 'store/slices/authSlice';
import { PrimaryButton, Link } from 'components/buttons';
import {
  DateRangeToggle,
  ScheduleField,
  SelectField,
  TextAreaField,
  TextInputField,
} from 'components/forms';
import {
  EMPTY_REMINDER_RANGE,
  REMINDER_RANGE_FIELD_KEYS,
  REMINDER_RANGE_SUPPORTED_FREQUENCIES,
  REMINDER_TYPES,
  REMINDER_TYPES_OPTIONS,
  REMINDER_FREQUENCIES_OPTIONS,
} from 'constants/reminders';
import { TIMEZONE_OPTIONS } from 'constants/date';
import { isValidMonthDay } from 'utils/date';
import { hasSeasonalRange } from 'utils/reminders';

function getRangeValidationMessage(label, month, day, yearRound) {
  if (yearRound) {
    return true;
  }

  if (month == null || day == null) {
    return `${label} date is required`;
  }

  if (!isValidMonthDay(month, day)) {
    return 'Invalid day for selected month';
  }

  return true;
}

export default function ReminderForm({
  defaultValues,
  onSubmit,
  loading,
  saving = false,
  requireChanges = false,
  submitLabel = 'Save',
  cancelLink = '/reminders'
}) {
  const user = useSelector((state) => state.auth.user);
  const settings = useSelector(selectSettings);
  const initialSchedule = defaultValues?.schedule || {};

  const {
    register,
    handleSubmit,
    control,
    setValue,
    clearErrors,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      type: REMINDER_TYPES.ONE_TIME,
      status: 'ACTIVE',
      ...defaultValues,
      schedule: {
        timezone: user.settings.timezone,
        ...initialSchedule,
        yearRound: initialSchedule.yearRound ?? !hasSeasonalRange(initialSchedule),
      },
    },
    shouldUnregister: true
  });

  const [
    subjectValue,
    messageValue,
    selectedType,
    selectedFrequency,
    yearRound,
    rangeStartMonth,
    rangeStartDay,
    rangeEndMonth,
    rangeEndDay,
  ] = useWatch({
    control,
    name: [
      'subject',
      'message',
      'type',
      'frequency',
      'schedule.yearRound',
      'schedule.rangeStartMonth',
      'schedule.rangeStartDay',
      'schedule.rangeEndMonth',
      'schedule.rangeEndDay',
    ],
  });

  const supportsSeasonalRange = selectedType === REMINDER_TYPES.REPEATING
    && REMINDER_RANGE_SUPPORTED_FREQUENCIES.includes(selectedFrequency);
  const isSubmitDisabled = saving || (requireChanges && !isDirty);
  const rangeFieldNames = [
    'schedule.rangeStartMonth',
    'schedule.rangeStartDay',
    'schedule.rangeEndMonth',
    'schedule.rangeEndDay',
  ];
  const startRangeError = errors.schedule?.rangeStartMonth?.message || errors.schedule?.rangeStartDay?.message;
  const endRangeError = errors.schedule?.rangeEndMonth?.message || errors.schedule?.rangeEndDay?.message;

  const handleYearRoundChange = (checked) => {
    setValue('schedule.yearRound', checked, {
      shouldDirty: true,
      shouldTouch: true,
    });

    if (checked) {
      clearErrors(rangeFieldNames);
    }
  };

  const handleRangeChange = (nextRange) => {
    const nextValues = {
      rangeStartMonth,
      rangeStartDay,
      rangeEndMonth,
      rangeEndDay,
      ...nextRange,
    };

    Object.entries(nextRange).forEach(([key, value]) => {
      setValue(`schedule.${key}`, value, {
        shouldDirty: true,
        shouldTouch: true,
      });
    });

    if (getRangeValidationMessage('Start', nextValues.rangeStartMonth, nextValues.rangeStartDay, yearRound) === true) {
      clearErrors(['schedule.rangeStartMonth', 'schedule.rangeStartDay']);
    }

    if (getRangeValidationMessage('End', nextValues.rangeEndMonth, nextValues.rangeEndDay, yearRound) === true) {
      clearErrors(['schedule.rangeEndMonth', 'schedule.rangeEndDay']);
    }
  };

  const handleFormSubmit = (formData) => {
    const normalizedData = {
      ...formData,
      schedule: {
        ...formData.schedule,
      },
    };

    delete normalizedData.schedule.yearRound;

    if (supportsSeasonalRange) {
      if (formData.schedule?.yearRound) {
        Object.assign(normalizedData.schedule, EMPTY_REMINDER_RANGE);
      }
    } else {
      REMINDER_RANGE_FIELD_KEYS.forEach((key) => {
        delete normalizedData.schedule[key];
      });
    }

    onSubmit(normalizedData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <fieldset disabled={saving} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Controller
            name="schedule.timezone"
            control={control}
            render={({ field }) => (
              <SelectField
                label="Timezone"
                options={TIMEZONE_OPTIONS}
                loading={loading}
                {...field}
              />
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <SelectField
                label="Type"
                options={REMINDER_TYPES_OPTIONS}
                loading={loading}
                {...field}
              />
            )}
          />

          {selectedType === REMINDER_TYPES.REPEATING && (
            <Controller
              name="frequency"
              control={control}
              rules={{ required: 'Type is required' }}
              render={({ field }) => (
                <SelectField
                  label="Frequency"
                  options={REMINDER_FREQUENCIES_OPTIONS}
                  errors={errors}
                  loading={loading}
                  {...field}
                />
              )}
            />
          )}
        </div>

        <ScheduleField
          control={control}
          type={selectedType}
          frequency={selectedFrequency}
          loading={loading}
          dateFormat={settings?.dateFormat}
          timeFormat={settings?.timeFormat}
        />

        {supportsSeasonalRange && (
          <div className="space-y-4">
            <Controller
              name="schedule.yearRound"
              control={control}
              render={() => null}
            />
            <Controller
              name="schedule.rangeStartMonth"
              control={control}
              rules={{
                validate: () => getRangeValidationMessage('Start', rangeStartMonth, rangeStartDay, yearRound),
              }}
              render={() => null}
            />
            <Controller
              name="schedule.rangeStartDay"
              control={control}
              rules={{
                validate: () => getRangeValidationMessage('Start', rangeStartMonth, rangeStartDay, yearRound),
              }}
              render={() => null}
            />
            <Controller
              name="schedule.rangeEndMonth"
              control={control}
              rules={{
                validate: () => getRangeValidationMessage('End', rangeEndMonth, rangeEndDay, yearRound),
              }}
              render={() => null}
            />
            <Controller
              name="schedule.rangeEndDay"
              control={control}
              rules={{
                validate: () => getRangeValidationMessage('End', rangeEndMonth, rangeEndDay, yearRound),
              }}
              render={() => null}
            />
            <DateRangeToggle
              yearRound={yearRound}
              onYearRoundChange={handleYearRoundChange}
              startMonth={rangeStartMonth}
              startDay={rangeStartDay}
              endMonth={rangeEndMonth}
              endDay={rangeEndDay}
              onChange={handleRangeChange}
              errors={{
                start: startRangeError,
                end: endRangeError,
              }}
              loading={loading}
            />
          </div>
        )}

        <TextInputField
          name="subject"
          labelText="Subject"
          register={register}
          registerOptions={{ required: 'Subject is required' }}
          errors={errors}
          placeholder="Meeting with team"
          maxLength={80}
          showCount
          currentLength={subjectValue?.length || 0}
          loading={loading}
        />

        <TextAreaField
          name="message"
          labelText="Message"
          register={register}
          registerOptions={{ required: 'Message is required' }}
          errors={errors}
          placeholder="Discuss project roadmap"
          maxLength={400}
          showCount
          currentLength={messageValue?.length || 0}
          loading={loading}
        />
      </fieldset>

      <div className="flex items-center justify-end gap-x-6">
        <Link to={cancelLink} className="text-sm font-semibold leading-6 text-white" loading={loading}>
          Cancel
        </Link>
        <PrimaryButton type="submit" loading={loading} saving={saving} disabled={isSubmitDisabled}>
          {submitLabel}
        </PrimaryButton>
      </div>
    </form>
  );
}
